import { useState, type InputHTMLAttributes, type ReactNode } from "react";
import {
  changePassword,
  changeUsername,
  confirmTotpSetup,
  disableTotp,
  startTotpSetup,
} from "@/api/client";

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="font-mono text-xs text-ink">{label}</div>
      <div className="flex flex-1 flex-col items-stretch gap-2 sm:max-w-xs sm:items-end">{children}</div>
    </div>
  );
}

function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="w-full rounded-md border border-line bg-raised px-2 py-1.5 font-mono text-xs text-ink outline-none focus:border-turquoise/60 sm:w-64"
    />
  );
}

function ActionButton({
  children,
  onClick,
  disabled,
  tone = "default",
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "danger";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`self-end rounded-md border px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider transition disabled:cursor-not-allowed disabled:opacity-40 ${
        tone === "danger"
          ? "border-sell/60 bg-sell/10 text-sell hover:bg-sell/20"
          : "border-turquoise/50 bg-turquoise/10 text-turquoise hover:bg-turquoise/20"
      }`}
    >
      {children}
    </button>
  );
}

function Status({ error, success }: { error: string | null; success: string | null }) {
  if (error) return <div className="font-mono text-[11px] text-sell">{error}</div>;
  if (success) return <div className="font-mono text-[11px] text-turquoise">{success}</div>;
  return null;
}

function UsernameSection() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await changeUsername(currentPassword, newUsername);
      setSuccess("Username updated.");
      setCurrentPassword("");
      setNewUsername("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update username");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-md border border-line-soft bg-raised/40 p-3">
      <FieldRow label="Current password">
        <TextInput type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
      </FieldRow>
      <FieldRow label="New username">
        <TextInput type="text" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} />
      </FieldRow>
      <div className="flex items-center justify-between gap-3">
        <Status error={error} success={success} />
        <ActionButton onClick={submit} disabled={busy || !currentPassword || !newUsername}>
          {busy ? "Saving…" : "Update Username"}
        </ActionButton>
      </div>
    </div>
  );
}

function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const submit = async () => {
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess("Password updated.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update password");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-md border border-line-soft bg-raised/40 p-3">
      <FieldRow label="Current password">
        <TextInput type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
      </FieldRow>
      <FieldRow label="New password (min 10 characters)">
        <TextInput type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
      </FieldRow>
      <FieldRow label="Confirm new password">
        <TextInput type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
      </FieldRow>
      <div className="flex items-center justify-between gap-3">
        <Status error={error} success={success} />
        <ActionButton onClick={submit} disabled={busy || !currentPassword || !newPassword}>
          {busy ? "Saving…" : "Update Password"}
        </ActionButton>
      </div>
    </div>
  );
}

function TwoFactorSection() {
  const [step, setStep] = useState<"idle" | "setup" | "disable">("idle");
  const [currentPassword, setCurrentPassword] = useState("");
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [provisioningUri, setProvisioningUri] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const beginSetup = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await startTotpSetup(currentPassword);
      setSetupToken(res.setupToken);
      setSecret(res.secret);
      setProvisioningUri(res.provisioningUri);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start 2FA setup");
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    if (!setupToken) return;
    setBusy(true);
    setError(null);
    try {
      await confirmTotpSetup(setupToken, totpCode);
      setSuccess("Two-factor authentication enabled.");
      setStep("idle");
      setSetupToken(null);
      setSecret(null);
      setProvisioningUri(null);
      setTotpCode("");
      setCurrentPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid authentication code");
    } finally {
      setBusy(false);
    }
  };

  const submitDisable = async () => {
    setBusy(true);
    setError(null);
    try {
      await disableTotp(currentPassword, totpCode);
      setSuccess("Two-factor authentication disabled.");
      setStep("idle");
      setCurrentPassword("");
      setTotpCode("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disable 2FA");
    } finally {
      setBusy(false);
    }
  };

  if (step === "idle") {
    return (
      <div className="space-y-3 rounded-md border border-line-soft bg-raised/40 p-3">
        <div className="font-mono text-[11px] leading-snug text-ink-faint">
          Set up a new authenticator app, or disable two-factor authentication entirely.
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Status error={error} success={success} />
          <div className="ml-auto flex gap-2">
            <ActionButton onClick={() => setStep("setup")}>Set Up 2FA</ActionButton>
            <ActionButton tone="danger" onClick={() => setStep("disable")}>
              Disable 2FA
            </ActionButton>
          </div>
        </div>
      </div>
    );
  }

  if (step === "setup") {
    return (
      <div className="space-y-3 rounded-md border border-line-soft bg-raised/40 p-3">
        {!setupToken ? (
          <>
            <FieldRow label="Current password">
              <TextInput type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </FieldRow>
            <div className="flex items-center justify-between gap-3">
              <Status error={error} success={null} />
              <div className="ml-auto flex gap-2">
                <ActionButton tone="danger" onClick={() => setStep("idle")}>
                  Cancel
                </ActionButton>
                <ActionButton onClick={beginSetup} disabled={busy || !currentPassword}>
                  {busy ? "Working…" : "Continue"}
                </ActionButton>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="font-mono text-[11px] leading-snug text-ink-faint">
              Scan this secret into your authenticator app (Google Authenticator, Authy, 1Password, etc.), then enter
              the 6-digit code it generates.
            </div>
            <div className="rounded-md border border-line bg-raised px-3 py-2 font-mono text-xs text-turquoise break-all">
              {secret}
            </div>
            {provisioningUri && (
              <div className="font-mono text-[10px] break-all text-ink-faint">{provisioningUri}</div>
            )}
            <FieldRow label="6-digit code">
              <TextInput
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
              />
            </FieldRow>
            <div className="flex items-center justify-between gap-3">
              <Status error={error} success={null} />
              <div className="ml-auto flex gap-2">
                <ActionButton tone="danger" onClick={() => setStep("idle")}>
                  Cancel
                </ActionButton>
                <ActionButton onClick={confirmSetup} disabled={busy || totpCode.length !== 6}>
                  {busy ? "Verifying…" : "Enable 2FA"}
                </ActionButton>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-line-soft bg-raised/40 p-3">
      <div className="font-mono text-[11px] leading-snug text-ink-faint">
        Disabling 2FA removes the extra login step. Confirm with your password and a current authenticator code.
      </div>
      <FieldRow label="Current password">
        <TextInput type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
      </FieldRow>
      <FieldRow label="6-digit code">
        <TextInput
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={totpCode}
          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
        />
      </FieldRow>
      <div className="flex items-center justify-between gap-3">
        <Status error={error} success={null} />
        <div className="ml-auto flex gap-2">
          <ActionButton tone="danger" onClick={() => setStep("idle")}>
            Cancel
          </ActionButton>
          <ActionButton
            tone="danger"
            onClick={submitDisable}
            disabled={busy || !currentPassword || totpCode.length !== 6}
          >
            {busy ? "Working…" : "Confirm Disable"}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

export default function SecuritySettings() {
  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-turquoise">Username</div>
        <UsernameSection />
      </div>
      <div>
        <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-turquoise">Password</div>
        <PasswordSection />
      </div>
      <div>
        <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-turquoise">
          Two-Factor Authentication
        </div>
        <TwoFactorSection />
      </div>
    </div>
  );
}
